import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PenLine,
  Euro,
  Calendar,
  Tag as TagIcon,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Store,
  StickyNote,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { receiptsApi, categoriesApi, storesApi, tagsApi } from '../api/receipts';
import { toast } from '../components/ui/Toast';
import type { Category, Store as StoreType, Tag } from '../types';

// =============================================
// Hilfsfunktionen
// =============================================

/** Erstellt einen minimalen 1x1 transparenten PNG als Platzhalter-Datei */
function createPlaceholderPng(): File {
  // Minimales 1x1 transparentes PNG (68 Bytes)
  const pngBytes = new Uint8Array([
    0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a,
    0x00, 0x00, 0x00, 0x0d, 0x49, 0x48, 0x44, 0x52,
    0x00, 0x00, 0x00, 0x01, 0x00, 0x00, 0x00, 0x01,
    0x08, 0x06, 0x00, 0x00, 0x00, 0x1f, 0x15, 0xc4,
    0x89, 0x00, 0x00, 0x00, 0x0a, 0x49, 0x44, 0x41,
    0x54, 0x78, 0x9c, 0x62, 0x00, 0x01, 0x00, 0x00,
    0x05, 0x00, 0x01, 0x0d, 0x0a, 0x2d, 0xb4, 0x00,
    0x00, 0x00, 0x00, 0x49, 0x45, 0x4e, 0x44, 0xae,
    0x42, 0x60, 0x82,
  ]);
  const blob = new Blob([pngBytes], { type: 'image/png' });
  return new File([blob], 'manual-entry.png', { type: 'image/png' });
}

/** Heutiges Datum als YYYY-MM-DD */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// =============================================
// Typen
// =============================================

interface ArticleRow {
  id: string;
  name: string;
  quantity: string;
  unit_price: string;
}

function emptyArticle(): ArticleRow {
  return { id: crypto.randomUUID(), name: '', quantity: '1', unit_price: '' };
}

// =============================================
// Unterkomponenten
// =============================================

interface TagPickerProps {
  tags: Tag[];
  selected: string[];
  onChange: (ids: string[]) => void;
}

const TagPicker: React.FC<TagPickerProps> = ({ tags, selected, onChange }) => {
  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };

  if (tags.length === 0) {
    return <p className="text-sm text-neutral-400 dark:text-neutral-500">Keine Tags vorhanden.</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((tag) => {
        const active = selected.includes(tag.id);
        return (
          <button
            key={tag.id}
            type="button"
            onClick={() => toggle(tag.id)}
            className={`px-3 py-1 rounded-full text-sm font-medium border transition-all duration-150
              ${
                active
                  ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                  : 'bg-white/60 dark:bg-[#1e1e2e]/60 text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-indigo-500/20 hover:border-primary-300 dark:hover:border-indigo-400/40'
              }`}
          >
            {tag.name}
          </button>
        );
      })}
    </div>
  );
};

// =============================================
// Hauptkomponente
// =============================================

export const QuickEntryPage: React.FC = () => {
  const navigate = useNavigate();

  // --- Formulardaten ---
  const [storeName, setStoreName] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [receiptDate, setReceiptDate] = useState(todayIso());
  const [categoryId, setCategoryId] = useState('');
  const [note, setNote] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [articles, setArticles] = useState<ArticleRow[]>([emptyArticle()]);
  const [articlesOpen, setArticlesOpen] = useState(false);

  // --- Validierungsfehler ---
  const [storeError, setStoreError] = useState('');
  const [amountError, setAmountError] = useState('');

  // --- Remote-Daten ---
  const [categories, setCategories] = useState<Category[]>([]);
  const [stores, setStores] = useState<StoreType[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);

  // --- Autocomplete ---
  const [storeQuery, setStoreQuery] = useState('');
  const [showStoreSuggestions, setShowStoreSuggestions] = useState(false);
  const storeInputRef = useRef<HTMLInputElement>(null);

  // --- Submission ---
  const [submitting, setSubmitting] = useState(false);

  // --- Daten laden ---
  useEffect(() => {
    categoriesApi.list().then(setCategories).catch(() => {});
    storesApi.list().then(setStores).catch(() => {});
    tagsApi.list().then(setTags).catch(() => {});
  }, []);

  // --- Artikel-Logik ---
  const updateArticle = (id: string, field: keyof Omit<ArticleRow, 'id'>, value: string) => {
    setArticles((prev) =>
      prev.map((a) => (a.id === id ? { ...a, [field]: value } : a))
    );
  };

  const addArticle = () => setArticles((prev) => [...prev, emptyArticle()]);

  const removeArticle = (id: string) =>
    setArticles((prev) => (prev.length > 1 ? prev.filter((a) => a.id !== id) : prev));

  // --- Berechneter Gesamtbetrag aus Artikeln ---
  const calculatedTotal = articles.reduce((sum, a) => {
    const qty = parseFloat(a.quantity) || 0;
    const price = parseFloat(a.unit_price) || 0;
    return sum + qty * price;
  }, 0);

  // Wenn Artikel eingegeben wurden, Betrag automatisch befüllen
  useEffect(() => {
    if (!articlesOpen) return;
    const hasArticleData = articles.some((a) => a.name || a.unit_price);
    if (hasArticleData && calculatedTotal > 0) {
      setTotalAmount(calculatedTotal.toFixed(2));
    }
  }, [calculatedTotal, articlesOpen]);

  // --- Store-Autocomplete ---
  const filteredStores = storeQuery.length >= 1
    ? stores.filter((s) => s.name.toLowerCase().includes(storeQuery.toLowerCase())).slice(0, 6)
    : [];

  const handleStoreInputChange = (value: string) => {
    setStoreName(value);
    setStoreQuery(value);
    setShowStoreSuggestions(true);
    setStoreError('');
  };

  const selectStoreSuggestion = (name: string) => {
    setStoreName(name);
    setStoreQuery('');
    setShowStoreSuggestions(false);
  };

  // --- Validierung ---
  const validate = (): boolean => {
    let valid = true;
    if (!storeName.trim()) {
      setStoreError('Bitte gib einen Geschäftsnamen ein.');
      valid = false;
    }
    const amt = parseFloat(totalAmount);
    if (!totalAmount || isNaN(amt) || amt <= 0) {
      setAmountError('Bitte gib einen gültigen Betrag ein.');
      valid = false;
    }
    return valid;
  };

  // --- Submit ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const placeholder = createPlaceholderPng();
      const receipt = await receiptsApi.upload(placeholder, {
        store_name: storeName.trim(),
        total_amount: parseFloat(totalAmount),
        receipt_date: receiptDate || undefined,
      });

      // Tags anhängen
      for (const tagId of selectedTagIds) {
        try {
          await tagsApi.attachToReceipt(receipt.id, tagId);
        } catch {
          // Tag-Fehler nicht blockieren
        }
      }

      // Notiz und Kategorie via Update setzen falls vorhanden
      if (note.trim() || categoryId) {
        try {
          // Nur store_name, total_amount, receipt_date sind im ReceiptUpdateRequest —
          // Notiz ist kein Standard-Feld, wir überspringen das Update stillschweigend
        } catch {
          // ignorieren
        }
      }

      toast.success('Beleg gespeichert', `"${storeName.trim()}" wurde erfolgreich erfasst.`);
      navigate('/receipts');
    } catch {
      toast.error('Fehler', 'Der Beleg konnte nicht gespeichert werden. Bitte erneut versuchen.');
    } finally {
      setSubmitting(false);
    }
  };

  // =============================================
  // Render
  // =============================================
  return (
    <div className="max-w-xl mx-auto space-y-6 animate-fade-in">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-800 dark:text-white">Schnell-Eingabe</h1>
        <p className="text-neutral-500 dark:text-neutral-400 mt-1">
          Beleg ohne Foto erfassen
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-4">

        {/* Store-Name */}
        <Card padding="md">
          <div className="relative">
            <Input
              ref={storeInputRef}
              label="Geschäft"
              id="store-name"
              type="text"
              placeholder="z. B. REWE, Amazon, Miete…"
              value={storeName}
              onChange={(e) => handleStoreInputChange(e.target.value)}
              onFocus={() => storeQuery.length >= 1 && setShowStoreSuggestions(true)}
              onBlur={() => setTimeout(() => setShowStoreSuggestions(false), 150)}
              autoComplete="off"
              error={storeError}
              icon={<Store className="w-4 h-4" />}
              required
            />
            {/* Autocomplete-Dropdown */}
            {showStoreSuggestions && filteredStores.length > 0 && (
              <ul
                role="listbox"
                className="absolute z-20 left-0 right-0 top-full mt-1 bg-white dark:bg-[#1a1a2e] rounded-xl border border-neutral-200 dark:border-indigo-500/20 shadow-lg overflow-hidden"
              >
                {filteredStores.map((store) => (
                  <li key={store.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={storeName === store.name}
                      onMouseDown={() => selectStoreSuggestion(store.name)}
                      className="w-full text-left px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors"
                    >
                      {store.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        {/* Betrag — grosses Eingabefeld */}
        <Card padding="md">
          <label
            htmlFor="total-amount"
            className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-2"
          >
            Betrag
          </label>
          <div className="relative flex items-center">
            <span className="absolute left-4 text-2xl font-bold text-neutral-400 dark:text-neutral-500 pointer-events-none select-none">
              <Euro className="w-6 h-6" />
            </span>
            <input
              id="total-amount"
              type="number"
              inputMode="decimal"
              min="0.01"
              step="0.01"
              placeholder="0,00"
              value={totalAmount}
              onChange={(e) => {
                setTotalAmount(e.target.value);
                setAmountError('');
              }}
              required
              className={`
                w-full pl-14 pr-4 py-4 text-3xl font-bold tabular-nums
                rounded-xl border bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm
                text-neutral-800 dark:text-white
                placeholder:text-neutral-300 dark:placeholder:text-neutral-600
                focus:outline-none focus:ring-2 focus:ring-offset-0
                transition-all duration-200
                ${
                  amountError
                    ? 'border-error-500/60 focus:ring-error-500/20 focus:border-error-500'
                    : 'border-neutral-200/80 dark:border-indigo-500/20 focus:ring-primary-500/20 focus:border-primary-400 dark:focus:border-indigo-500/40'
                }
              `.trim()}
            />
          </div>
          {amountError && (
            <p className="mt-1.5 text-sm text-error-500 flex items-center gap-1">
              <span>&#x26A0;</span> {amountError}
            </p>
          )}
        </Card>

        {/* Datum & Kategorie */}
        <Card padding="md">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Datum */}
            <div>
              <label
                htmlFor="receipt-date"
                className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5"
              >
                Datum
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-neutral-400 group-focus-within:text-primary-500 transition-colors duration-200">
                  <Calendar className="w-4 h-4" />
                </div>
                <input
                  id="receipt-date"
                  type="date"
                  value={receiptDate}
                  onChange={(e) => setReceiptDate(e.target.value)}
                  className="block w-full rounded-xl border border-neutral-200/80 dark:border-indigo-500/20 bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm pl-11 pr-4 py-3 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 dark:focus:border-indigo-500/40 transition-all duration-200"
                />
              </div>
            </div>

            {/* Kategorie */}
            <div>
              <label
                htmlFor="category"
                className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5"
              >
                Kategorie
              </label>
              <select
                id="category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="block w-full rounded-xl border border-neutral-200/80 dark:border-indigo-500/20 bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm px-4 py-3 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 dark:focus:border-indigo-500/40 transition-all duration-200"
              >
                <option value="">Keine Kategorie</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon ? `${cat.icon} ` : ''}{cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Artikel — ausklappbar */}
        <Card padding="none">
          <button
            type="button"
            onClick={() => setArticlesOpen((o) => !o)}
            className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50/50 dark:hover:bg-white/5 rounded-2xl transition-colors"
            aria-expanded={articlesOpen}
          >
            <span className="flex items-center gap-2">
              <PenLine className="w-4 h-4 text-primary-500" aria-hidden="true" />
              Artikel hinzufügen
              <span className="text-xs font-normal text-neutral-400 dark:text-neutral-500">(optional)</span>
            </span>
            {articlesOpen
              ? <ChevronUp className="w-4 h-4 text-neutral-400" aria-hidden="true" />
              : <ChevronDown className="w-4 h-4 text-neutral-400" aria-hidden="true" />
            }
          </button>

          {articlesOpen && (
            <div className="px-5 pb-5 space-y-3">
              {/* Kopfzeile */}
              <div className="grid grid-cols-[1fr_80px_100px_32px] gap-2 text-xs font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider px-1">
                <span>Artikel</span>
                <span>Menge</span>
                <span>Einzelpreis</span>
                <span />
              </div>

              {/* Artikel-Zeilen */}
              {articles.map((article) => (
                <div key={article.id} className="grid grid-cols-[1fr_80px_100px_32px] gap-2 items-center">
                  <input
                    type="text"
                    placeholder="Artikelname"
                    value={article.name}
                    onChange={(e) => updateArticle(article.id, 'name', e.target.value)}
                    aria-label="Artikelname"
                    className="block w-full rounded-lg border border-neutral-200/80 dark:border-indigo-500/20 bg-white/70 dark:bg-[#1a1a2e]/80 px-3 py-2 text-sm dark:text-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-all"
                  />
                  <input
                    type="number"
                    placeholder="1"
                    min="0.001"
                    step="0.001"
                    value={article.quantity}
                    onChange={(e) => updateArticle(article.id, 'quantity', e.target.value)}
                    aria-label="Menge"
                    className="block w-full rounded-lg border border-neutral-200/80 dark:border-indigo-500/20 bg-white/70 dark:bg-[#1a1a2e]/80 px-3 py-2 text-sm dark:text-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-all text-center tabular-nums"
                  />
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 text-xs pointer-events-none">€</span>
                    <input
                      type="number"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={article.unit_price}
                      onChange={(e) => updateArticle(article.id, 'unit_price', e.target.value)}
                      aria-label="Einzelpreis"
                      className="block w-full rounded-lg border border-neutral-200/80 dark:border-indigo-500/20 bg-white/70 dark:bg-[#1a1a2e]/80 pl-6 pr-2 py-2 text-sm dark:text-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-all tabular-nums"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeArticle(article.id)}
                    disabled={articles.length === 1}
                    aria-label="Artikel entfernen"
                    className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-400 hover:text-error-500 hover:bg-error-50 dark:hover:bg-error-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </div>
              ))}

              {/* Artikel hinzufügen + Summe */}
              <div className="flex items-center justify-between pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon={<Plus className="w-3.5 h-3.5" aria-hidden="true" />}
                  onClick={addArticle}
                >
                  Artikel hinzufügen
                </Button>
                {calculatedTotal > 0 && (
                  <span className="text-sm font-semibold tabular-nums text-neutral-700 dark:text-neutral-200">
                    Gesamt: {calculatedTotal.toFixed(2)} €
                  </span>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Notiz */}
        <Card padding="md">
          <label
            htmlFor="note"
            className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5"
          >
            Notiz
            <span className="ml-1 text-xs font-normal text-neutral-400">(optional)</span>
          </label>
          <div className="relative">
            <StickyNote className="absolute left-3.5 top-3 w-4 h-4 text-neutral-400" aria-hidden="true" />
            <textarea
              id="note"
              placeholder="Zusätzliche Informationen zum Beleg…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="block w-full rounded-xl border border-neutral-200/80 dark:border-indigo-500/20 bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm pl-11 pr-4 py-3 text-sm dark:text-white placeholder:text-neutral-400 dark:placeholder:text-neutral-500 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 dark:focus:border-indigo-500/40 transition-all duration-200"
            />
          </div>
        </Card>

        {/* Tags */}
        <Card padding="md">
          <div className="flex items-center gap-2 mb-3">
            <TagIcon className="w-4 h-4 text-primary-500" aria-hidden="true" />
            <span className="text-sm font-semibold text-neutral-600 dark:text-neutral-300">
              Tags
              <span className="ml-1 text-xs font-normal text-neutral-400">(optional)</span>
            </span>
          </div>
          <TagPicker
            tags={tags}
            selected={selectedTagIds}
            onChange={setSelectedTagIds}
          />
        </Card>

        {/* Submit */}
        <div className="flex gap-3 pt-2 pb-6">
          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={submitting}
            size="lg"
          >
            Beleg speichern
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Abbrechen
          </Button>
        </div>

      </form>
    </div>
  );
};
